import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "../lib/api";
import { ArrowLeft, AlertTriangle } from "lucide-react";

const SECTIONS = [
  { key: "identification", title: "Identification" },
  { key: "parts_used", title: "Parts Used" },
  { key: "preparation", title: "Preparation" },
  { key: "dosage", title: "Dosage" },
  { key: "benefits", title: "Benefits" },
  { key: "lookalikes", title: "Lookalikes" },
];

export default function PlantDetail() {
  const { slug } = useParams();
  const [plant, setPlant] = useState(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api.get(`/plants/${slug}`).then((r) => setPlant(r.data)).catch((e) => setError(e.response?.data?.detail || "Could not load plant"));
  }, [slug]);

  if (error) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[var(--ink-soft)]">{error}</div>;
  if (!plant) return <div className="max-w-3xl mx-auto px-6 py-20 text-center text-[var(--ink-soft)]">Loading…</div>;

  return (
    <article className="max-w-4xl mx-auto px-6 md:px-10 py-16 md:py-20">
      <Link to="/library" className="btn-ghost inline-flex items-center gap-2 text-sm">
        <ArrowLeft className="w-4 h-4" /> Back to the library
      </Link>

      <header className="mt-8 grid md:grid-cols-12 gap-10 items-start">
        <div className="md:col-span-7">
          <p className="font-mono text-sm italic text-[var(--sage)]">{plant.scientific_name}</p>
          <h1 className="font-serif text-5xl md:text-6xl mt-2 leading-tight">{plant.common_name}</h1>
          <hr className="divider-rule" />
          <p className="text-lg text-[var(--ink-soft)] leading-relaxed">{plant.summary}</p>
          <div className="mt-5 flex flex-wrap gap-2">
            {(plant.categories || []).map((c) => (
              <span key={c} className="chip">{c.replace("_", " ")}</span>
            ))}
          </div>
        </div>
        <div className="md:col-span-5">
          <div className="frame-border">
            <img src={plant.image_url} alt={plant.common_name} className="w-full aspect-[4/5] object-cover" />
          </div>
          <p className="font-mono text-xs text-[var(--muted)] mt-3 italic text-right">{plant.scientific_name}</p>
        </div>
      </header>

      <div className="mt-16 grid md:grid-cols-12 gap-12">
        <div className="md:col-span-8 space-y-10">
          {SECTIONS.map((s) =>
            plant[s.key] ? (
              <section key={s.key} data-testid={`plant-section-${s.key}`}>
                <p className="label-eyebrow">{s.title}</p>
                <p className="font-serif text-2xl md:text-3xl mt-2 leading-snug text-[var(--ink)]" style={{ fontWeight: 400 }}>
                  {plant[s.key]}
                </p>
              </section>
            ) : null
          )}
        </div>
        <aside className="md:col-span-4">
          {plant.cautions && (
            <div className="card-journal border-[var(--terracotta)]" data-testid="plant-cautions">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-[var(--terracotta)]" strokeWidth={1.75} />
                <p className="label-eyebrow text-[var(--terracotta)]">Cautions</p>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-[var(--ink-soft)]">{plant.cautions}</p>
            </div>
          )}
        </aside>
      </div>
    </article>
  );
}
